import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { 
  Home, 
  Compass, 
  PencilRuler, 
  ArrowRight, 
  Search,
  Building2,
  Sparkles,
  CheckCircle2,
  ExternalLink,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PlansPage = () => {
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  // Fetch collections and settings from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch active collections
        const collectionsRes = await fetch(`${API_URL}/api/site-settings/plans/collections/active`);
        const collectionsData = await collectionsRes.json();
        const activeCollections = collectionsData.collections || [];
        setCollections(activeCollections);
        
        // Don't auto-select any collection - user clicks to reveal

        // Fetch page settings
        const settingsRes = await fetch(`${API_URL}/api/site-settings/plans`);
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
      } catch (error) {
        console.error('Error fetching plans data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeCollectionData = collections.find(c => c.id === activeCollection);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section - Matching other pages style */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Home className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            House Plans
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Browse our curated collections of ready-to-build house plans, search thousands more, 
            or work with our architect partner for a fully custom design.
          </p>
        </div>
      </section>

      {/* Options Overview */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Option 1: Curated Collections */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                <Compass className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Curated Collections</h3>
              <p className="text-slate-600 mb-4">
                Hand-picked plans we love, organized by style. Perfect for finding inspiration fast.
              </p>
              <a href="#collections" className="text-cyan-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                Browse Collections <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Option 2: Full Search */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Search 25,000+ Plans</h3>
              <p className="text-slate-600 mb-4">
                Can&apos;t find what you&apos;re looking for? Search the full Architectural Designs catalog.
              </p>
              <a href="#search" className="text-purple-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                Search All Plans <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Option 3: Custom Design */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <PencilRuler className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Custom Design</h3>
              <p className="text-slate-600 mb-4">
                Want something truly unique? Our architect partner can create a custom plan just for you.
              </p>
              <a href="#custom" className="text-amber-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Curated Collections Section */}
      <section id="collections" className="py-20 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Curated Collections
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We&apos;ve hand-selected plans we believe represent exceptional design and value. 
              Click a style below to explore.
            </p>
          </div>

          {/* 3-Column Grid of Collection Cards */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-16">
              <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Collections Coming Soon</h3>
              <p className="text-slate-500">We&apos;re curating our favorite plans. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {collections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => setActiveCollection(activeCollection === collection.id ? null : collection.id)}
                    data-testid={`collection-card-${collection.id}`}
                    className={`group relative overflow-hidden rounded-2xl h-64 text-left transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-2xl ${
                      activeCollection === collection.id
                        ? 'ring-4 ring-cyan-400 shadow-xl'
                        : ''
                    }`}
                  >
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{ 
                        backgroundImage: collection.cover_image 
                          ? `url(${collection.cover_image})` 
                          : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                      }}
                    />
                    
                    {/* Dark overlay gradient */}
                    <div className={`absolute inset-0 transition-opacity duration-300 ${
                      activeCollection === collection.id
                        ? 'bg-gradient-to-t from-cyan-900/90 via-cyan-900/60 to-cyan-900/30'
                        : 'bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent group-hover:from-slate-900/95'
                    }`} />
                    
                    {/* Content */}
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">
                        {collection.name}
                      </h3>
                      <p className={`text-sm transition-colors ${
                        activeCollection === collection.id ? 'text-cyan-200' : 'text-slate-300'
                      }`}>
                        {activeCollection === collection.id ? 'Click to close' : 'Click to explore plans'}
                      </p>
                    </div>
                    
                    {/* Selected indicator badge */}
                    {activeCollection === collection.id && (
                      <div className="absolute top-4 right-4 bg-cyan-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                        Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Selected Collection Embed - Shows below grid when a card is clicked */}
              {activeCollection && activeCollectionData && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{activeCollectionData.name}</h3>
                    {activeCollectionData.description && (
                      <p className="text-slate-600">{activeCollectionData.description}</p>
                    )}
                  </div>
                  <div 
                    className="w-full bg-slate-50 rounded-2xl overflow-hidden"
                    data-testid="collection-embed-container"
                    dangerouslySetInnerHTML={{ __html: activeCollectionData.embed_code || '' }}
                  />
                  <p className="text-center text-sm text-slate-500 mt-6">
                    Plans provided by <a href="https://www.architecturaldesigns.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Architectural Designs</a>. 
                    Click any plan to view details and pricing.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Full Search Section */}
      <section id="search" className="py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
              <Search className="w-4 h-4 text-purple-300" />
              <span className="text-purple-200 text-sm font-medium">25,000+ Plans</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Can&apos;t Find What You&apos;re Looking For?
            </h2>
            <p className="text-lg text-purple-200 max-w-2xl mx-auto mb-8">
              Search the complete Architectural Designs catalog with over 25,000 house plans. 
              Filter by style, size, bedrooms, and more to find your perfect match.
            </p>
            <a
              href="https://www.architecturaldesigns.com/house-plans/search"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-900 font-bold rounded-xl hover:bg-purple-50 transition-colors shadow-xl"
            >
              <Search className="w-5 h-5" />
              Search All Plans
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          </div>

          {/* Search Features */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: Home, label: 'By Style', desc: 'Craftsman, Modern, Farmhouse & more' },
              { icon: Building2, label: 'By Size', desc: 'From cozy cabins to grand estates' },
              { icon: Compass, label: 'By Features', desc: 'Wrap-around porch, bonus room, etc.' },
              { icon: Sparkles, label: 'By Collection', desc: 'New plans, best sellers, exclusives' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <feature.icon className="w-8 h-8 text-purple-300 mx-auto mb-3" />
                <h4 className="font-semibold text-white mb-1">{feature.label}</h4>
                <p className="text-sm text-purple-200">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Design Section */}
      <section id="custom" className="py-20 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <PencilRuler className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700 text-sm font-medium">Custom Design-Build</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Want Something Truly Unique?
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                If pre-designed plans don&apos;t capture your vision, our design-build approach offers a 
                streamlined path to a fully custom home. We&apos;ve partnered with{' '}
                <strong>Ryan Stell at Objectechne Studio</strong> to deliver exceptional custom designs 
                with the efficiency of integrated construction planning.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Work directly with an experienced architect who understands construction',
                  'Seamless handoff from design to build with C3 Contracting',
                  'Cost-aware design process - no surprises when it comes to building',
                  'Personalized attention to your lifestyle, site, and budget',
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  size="lg" 
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
                >
                  <a href="/contact?subject=Custom%20Design%20Inquiry">
                    Start Custom Design Conversation
                  </a>
                </Button>
              </div>
            </div>

            {/* Partner Card */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 lg:p-10 shadow-xl border border-slate-200">
              <div className="text-center mb-8">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
                  Our Architecture Partner
                </h3>
                {/* Logo Placeholder */}
                <div className="w-48 h-24 bg-slate-200 rounded-lg mx-auto flex items-center justify-center mb-6">
                  <span className="text-slate-400 text-sm">Objectechne Studio Logo</span>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-2">Objectechne Studio</h4>
                <p className="text-slate-600">Ryan Stell, Architect</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h5 className="font-semibold text-slate-900 mb-3">The Design-Build Advantage</h5>
                <p className="text-slate-600 text-sm leading-relaxed">
                  When your architect and builder work together from day one, you get a home designed 
                  not just for beauty, but for buildability. No costly surprises, no design-to-construction 
                  disconnects. Just a smooth process from dream to done.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-cyan-600">15+</div>
                    <div className="text-sm text-slate-500">Years Experience</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-600">Custom</div>
                    <div className="text-sm text-slate-500">Every Project</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Build Your Dream Home?
          </h2>
          <p className="text-lg text-cyan-100 mb-8">
            Whether you&apos;ve found the perfect plan or want to discuss custom options, 
            we&apos;re here to help you take the next step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold px-8"
            >
              <a href="/contact">Contact Us</a>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 font-semibold px-8"
            >
              <a href="/estimator">Get a Project Estimate</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlansPage;
